import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EdlModalEvidence } from './edl-evidence-modal.component';
import { EdlModalConfirmNotes } from './edl-modal-confirm-notes/edl-modal-confirm-notes.component';
import { EdlModalSubmit } from './edl-modal-submit.component';
import { EdlModalSendMessage } from './edl-send-message-modal.component';
import { EdllModalNotesComponent } from './edl-modal-notes/edl-modal-notes.component';
import { ArchiveWarnings } from '../model/archive-warnings.model';
import { EdlModalArchiveItem } from './edl-modal-archive-item/edl-modal-archive-item.component';
import { QuestionnaireMasterStatus } from '../enums/questionnaire-master-status.enum';
import { EdlModalDuplicateItem } from './edl-modal-duplicate-item/edl-modal-duplicate-item.component';
import { DuplicateItemQfmSections } from './edl-modal-duplicate-item/models/duplicate-item-qfm-sections.model';
import { QuestionnaireMasterAvailability } from '../enums/questionnaire-master-availability.enum';
import { EdlModalAdvancedRosterSearch } from './edl-advanced-roster-search/edl-modal-advanced-roster-search.component';
import { AdvancedRosterSearch } from '../model/advanced-roster-search.model';
import { EdlModalApiResponse } from './edl-modal-api-response/edl-modal-api-response.component';
import { EdlCommonModalService, EdlModalUserDirectoryService, Group, RosterApplyGuard, RosterContext, User } from '@edl-development/edl-common';
import { CplItemPermissions } from '../model/enum/cpl-item-permissions.enum';
import { FileAttachmentType } from '../supervision-management/compliance-items/compliance-item/enums/file-attachment-type.enum';
import { EdlModalRosterPrintComponent } from './edl-modal-roster-print/edl-modal-roster-print.component';
import { UserRequestFilterStatus } from '../enums/user-request/user-request-filter-status.enum';
import { EdlSelectOption } from '../model/common/edl-select-option.model';

// NgbModal natively restores focus to the triggering element on modal dismiss (WCAG 2.4.3).
// No manual focus-restoration logic is needed in this service or in individual modal components.
@Injectable()
export class ModalService extends EdlCommonModalService {
    constructor(
        protected ngbModal: NgbModal,
        private edlModalUserDirectoryService: EdlModalUserDirectoryService
    ) {
        super(ngbModal);
    }


    archvieItem(title: string, message: string, archiveWarnings: ArchiveWarnings) {
        this.currentModal = this.ngbModal.open(EdlModalArchiveItem);
        this.currentModal.componentInstance.title = title;
        this.currentModal.componentInstance.message = message;
        this.currentModal.componentInstance.archiveWarnings = archiveWarnings;

        return this.currentModal.result;
    }

    confirmNotes({
        title,
        message,
        textAreaInstructions,
        noteLabel,
        noteRequired
    }: {
        title: string;
        message: string;
        textAreaInstructions?: string;
        noteLabel?: string;
        noteRequired?: boolean;
    }) {
        this.currentModal = this.ngbModal.open(EdlModalConfirmNotes);
        this.currentModal.componentInstance.title = title;
        this.currentModal.componentInstance.message = message;
        this.currentModal.componentInstance.textAreaInstructions = textAreaInstructions;
        if (noteLabel != null) {
            this.currentModal.componentInstance.noteLabel = noteLabel;
        }
        if (noteRequired != null) {
            this.currentModal.componentInstance.noteRequired = noteRequired;
        }

        return this.currentModal.result;
    }

    notes({
        typeOfNote,
        typeId,
        notesToInclude,
        isReadOnly
    }: {
        typeOfNote: number,
        typeId: string,
        notesToInclude: number,
        isReadOnly?: boolean
    }) {
        this.currentModal = this.ngbModal.open(EdllModalNotesComponent, {
            size: 'lg',
            backdrop: 'static',
            windowClass: 'edl-modal-notes'
        });
        this.currentModal.componentInstance.typeOfNote = typeOfNote;
        this.currentModal.componentInstance.typeId = typeId;
        this.currentModal.componentInstance.notesToInclude = notesToInclude;
        this.currentModal.componentInstance.isReadOnly = isReadOnly;

        return this.currentModal.result;
    }

    apiResponse({
        responses,
        title,
        successLabel,
        failedLabel
    }: {
        responses: { firstName: string, lastName: string, description: string; statusCode: number; message: string; }[];
        title: string;
        successLabel: string;
        failedLabel: string;
    }) {
        let modalRef = this.ngbModal.open(EdlModalApiResponse, {
            size: 'lg',
            backdrop: 'static'
        });

        modalRef.componentInstance.responses = responses;
        modalRef.componentInstance.title = title;
        modalRef.componentInstance.successLabel = successLabel;
        modalRef.componentInstance.failedLabel = failedLabel;

        return modalRef.result;
    }

    evidence({
        type,
        typeId,
        isReadOnly = false,
        isDeleteDisabled = false,
        isUploadDisabled = false
    }: {
        type: FileAttachmentType,
        typeId: string,
        isReadOnly?: boolean,
        isDeleteDisabled?: boolean,
        isUploadDisabled?: boolean
    }) {
        this.currentModal = this.ngbModal.open(EdlModalEvidence, {
            size: 'lg',
            backdrop: 'static'
        });
        this.currentModal.componentInstance.fileType = type;
        this.currentModal.componentInstance.fileTypeId = typeId;
        this.currentModal.componentInstance.isReadOnly = isReadOnly;
        this.currentModal.componentInstance.isDeleteDisabled = isDeleteDisabled
        this.currentModal.componentInstance.isUploadDisabled = isUploadDisabled;
        return this.currentModal.result;
    }
    
    submit() {
        this.currentModal = this.ngbModal.open(EdlModalSubmit);
        return this.currentModal.result;
    }

    sendMessage(target: number, typeId: string, type: number) {
        this.currentModal = this.ngbModal.open(EdlModalSendMessage, {
            size: 'lg',
            backdrop: 'static'
        });

        this.currentModal.componentInstance.type = type;
        this.currentModal.componentInstance.target = target;
        this.currentModal.componentInstance.typeId = typeId;
        return this.currentModal.result;
    }
    sendBulkMessage(target: number, typeIds: Array<string>, type: number) {
        this.currentModal = this.ngbModal.open(EdlModalSendMessage, {
            size: 'lg',
            backdrop: 'static'
        });

        this.currentModal.componentInstance.type = type;
        this.currentModal.componentInstance.target = target;
        this.currentModal.componentInstance.typeIds = typeIds;
        return this.currentModal.result;
    }
    duplicateItem({
        modalTitle,
        itemTitle,
        maxTitleLength,
        questionnaireSections = [],
        status = QuestionnaireMasterStatus.PUBLISHED,
        availability = QuestionnaireMasterAvailability.ALL_ITEMS,
        isTemplate,
        toTemplate,
        cplItemPermissions = []
    }:
        {
            modalTitle: string;
            itemTitle: string;
            maxTitleLength: number;
            questionnaireSections?: DuplicateItemQfmSections[];
            status?: QuestionnaireMasterStatus;
            availability?: QuestionnaireMasterAvailability;
            isTemplate: boolean,
            toTemplate: boolean;
            cplItemPermissions?: CplItemPermissions[]
        }) {
        this.currentModal = this.ngbModal.open(EdlModalDuplicateItem, {
            size: 'lg',
            backdrop: 'static',
            windowClass: 'edl-modal-duplicate-item'
        });

        this.currentModal.componentInstance.modalTitle = modalTitle;
        this.currentModal.componentInstance.itemTitle = itemTitle;
        this.currentModal.componentInstance.maxTitleLength = maxTitleLength;
        this.currentModal.componentInstance.questionnaireSections = questionnaireSections;
        this.currentModal.componentInstance.status = status;
        this.currentModal.componentInstance.availability = availability;
        this.currentModal.componentInstance.isTemplate = isTemplate;
        this.currentModal.componentInstance.toTemplate = toTemplate;
        this.currentModal.componentInstance.cplItemPermissions = cplItemPermissions;

        return this.currentModal.result;
    }

    advancedRosterSearch(
        {
            initialSearch,
            Statuses,
            Approvers,
            isAct48Enabled,
            Groups
        }: {
            initialSearch: AdvancedRosterSearch,
            Statuses: Readonly<EdlSelectOption<UserRequestFilterStatus | null>[]>,
            Approvers: Readonly<{ UserID: string, DisplayName: string }[]>,
            isAct48Enabled: boolean
            Groups?: Readonly<Group[]>
        }) {
        this.currentModal = this.ngbModal.open(EdlModalAdvancedRosterSearch);
        this.currentModal.componentInstance.initialSearch = initialSearch;
        this.currentModal.componentInstance.Statuses = Statuses;
        this.currentModal.componentInstance.Approvers = Approvers;
        this.currentModal.componentInstance.isAct48Enabled = isAct48Enabled;
        this.currentModal.componentInstance.Groups = Groups;

        return this.currentModal.result;
    }

    printRoster(title:string) {
        this.currentModal = this.ngbModal.open(EdlModalRosterPrintComponent);
        this.currentModal.componentInstance.title = title;
        return this.currentModal.result;
    }

    userDirectory({
        users = [],
        portalId,
        title,
        singleUser,
        searchType,
        AccessRoles,
        Groups,
        PortalRoleNames,
        ExcludePortalRoleNames,
        rosterContext,
        applyLabel,
        applyGuard,
        footerNote
    }: {
            users: User[];
            portalId: number;
            title?: string;
            singleUser?: boolean;
            searchType?: number;
            AccessRoles?: number[];
            Groups?: Group[];
            PortalRoleNames?: string[];
            ExcludePortalRoleNames?: string[];
            rosterContext?: RosterContext;
            applyLabel?: string;
            applyGuard?: RosterApplyGuard;
            footerNote?: string;
        }) {
        return this.edlModalUserDirectoryService.userDirectory({
            users,
            portalId,
            title,
            singleUser,
            searchType,
            AccessRoles,
            Groups,
            PortalRoleNames,
            ExcludePortalRoleNames,
            rosterContext,
            applyLabel,
            applyGuard,
            footerNote
        });
    }
}